import { useEffect, useRef, useState } from "react"

interface IOptions {
  /** Per-character step in ms (both backspace + type). Default 45. */
  stepMs?: number
}

interface IResult {
  /** The text to render this frame — mid-animation it's a partial string. */
  text: string
  /** True while a backspace→retype animation is in flight. */
  animating: boolean
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Typewriter rename. When `target` changes, animates a backspace down to the
 * longest common prefix of the old + new value, then types out the new value —
 * e.g. `Speaker_1` → (backspace) → `Gerard`. The first value (mount) and any
 * change while the user prefers reduced motion are applied instantly.
 *
 * Returns the partial `text` for the current frame and an `animating` flag the
 * caller can use to show a caret. Part of the app-wide motion design language
 * (see feedback_micro_animation_design_language) — keep it subtle + purposeful.
 */
export function useTypewriterRename(target: string, options?: IOptions): IResult {
  const stepMs = options?.stepMs ?? 45
  const [text, setText] = useState<string>(target)
  const [animating, setAnimating] = useState<boolean>(false)
  const prevTargetRef = useRef<string>(target)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const prev = prevTargetRef.current
    prevTargetRef.current = target

    // No-op on first mount / unchanged target.
    if (target === prev) return
    // Reduced motion → swap instantly, no caret.
    if (prefersReducedMotion()) {
      setText(target)
      setAnimating(false)
      return
    }

    // Longest common prefix — the typewriter only deletes the differing tail.
    let common = 0
    const max = Math.min(prev.length, target.length)
    while (common < max && prev[common] === target[common]) common += 1

    const frames: string[] = []
    for (let i = prev.length; i > common; i -= 1) frames.push(prev.slice(0, i - 1))
    for (let i = common + 1; i <= target.length; i += 1) frames.push(target.slice(0, i))

    if (frames.length === 0) {
      setText(target)
      return
    }

    setAnimating(true)
    let idx = 0
    const tick = (): void => {
      setText(frames[idx] ?? target)
      idx += 1
      if (idx < frames.length) {
        timerRef.current = setTimeout(tick, stepMs)
      } else {
        setText(target)
        setAnimating(false)
      }
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    tick()

    return (): void => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [target, stepMs])

  return { text, animating }
}
